import { Controller } from "@hotwired/stimulus"

// Drag a rectangle over the live snapshot to restrict the timelapse search to a region.
//
// The box stores motion boxes in native source-frame pixels, so the rectangle is kept in that
// space too (`this.rect`) and only converted to CSS pixels when painting the overlay. Drawing
// stays disabled until timelapses--search reports a real snapshot — a rectangle drawn over the
// placeholder would be measured against the wrong frame.
export default class extends Controller {
  static targets = ['frame', 'overlay', 'image', 'coords', 'x1', 'y1', 'x2', 'y2']

  connect() {
    this.rect = null
    this.live = false
    this.dragging = false
    this.scaleX = 0
    this.scaleY = 0

    // the placeholder may already be decoded, in which case its load event is long gone
    if (this.imageTarget.complete) { this.measure() }

    this.render()
  }

  // timelapses--search:imageLoaded
  enable() {
    this.live = true
    this.clear()
  }

  // timelapses--search:imageError
  disable() {
    this.live = false
    this.clear()
  }

  // load on the <img> — fires once the new snapshot is decoded, so naturalWidth is trustworthy
  imageLoaded() {
    this.measure()
    this.render()
  }

  // resize@window
  reposition() {
    this.measure()
    this.render()
  }

  startDrag(event) {
    // the pill is hidden on page load, so earlier measurements may have seen a 0×0 image
    this.measure()
    if (!this.live || !this.scaleX) { return }

    this.dragging = true
    this.origin = this.pointAt(event)
    this.rect = null
    this.frameTarget.setPointerCapture(event.pointerId)
  }

  drag(event) {
    if (!this.dragging) { return }
    event.preventDefault()

    const point = this.pointAt(event)

    this.rect = {
      x1: Math.round(Math.min(this.origin.x, point.x) * this.scaleX),
      y1: Math.round(Math.min(this.origin.y, point.y) * this.scaleY),
      x2: Math.round(Math.max(this.origin.x, point.x) * this.scaleX),
      y2: Math.round(Math.max(this.origin.y, point.y) * this.scaleY)
    }

    this.render()
  }

  endDrag(event) {
    if (!this.dragging) { return }

    this.dragging = false
    if (this.frameTarget.hasPointerCapture(event.pointerId)) {
      this.frameTarget.releasePointerCapture(event.pointerId)
    }

    // a click without a drag means "no region filter", not "keep the previous one"
    if (!this.rect || this.rect.x2 - this.rect.x1 < 1 || this.rect.y2 - this.rect.y1 < 1) {
      this.clear()
    }
  }

  clear() {
    this.rect = null
    this.render()
  }

  measure() {
    const image = this.imageTarget

    this.scaleX = image.clientWidth ? image.naturalWidth / image.clientWidth : 0
    this.scaleY = image.clientHeight ? image.naturalHeight / image.clientHeight : 0
  }

  pointAt(event) {
    const bounds = this.frameTarget.getBoundingClientRect()

    return {
      x: clamp(event.clientX - bounds.left, 0, bounds.width),
      y: clamp(event.clientY - bounds.top, 0, bounds.height)
    }
  }

  render() {
    const drawable = this.rect && this.scaleX && this.scaleY

    this.overlayTarget.style.display = drawable ? 'block' : 'none'

    if (drawable) {
      this.overlayTarget.style.left   = `${this.rect.x1 / this.scaleX}px`
      this.overlayTarget.style.top    = `${this.rect.y1 / this.scaleY}px`
      this.overlayTarget.style.width  = `${(this.rect.x2 - this.rect.x1) / this.scaleX}px`
      this.overlayTarget.style.height = `${(this.rect.y2 - this.rect.y1) / this.scaleY}px`
    }

    this.writeCoordinates(drawable ? this.rect : null)
  }

  writeCoordinates(rect) {
    this.x1Target.value = rect ? rect.x1 : ''
    this.y1Target.value = rect ? rect.y1 : ''
    this.x2Target.value = rect ? rect.x2 : ''
    this.y2Target.value = rect ? rect.y2 : ''

    if (this.hasCoordsTarget) {
      this.coordsTarget.innerText = rect ? `${rect.x1}, ${rect.y1} — ${rect.x2}, ${rect.y2}` : ''
    }
  }
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)
